import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { Card } from "@/components/ui/card";
import { PiggyBank } from "lucide-react";

/**
 * CostGapCard
 * -----------------------------------------------------------------------------
 * Reframes a cheap-tuition degree (Germany public, France public, etc.) against
 * the UK & US medians. Auto-triggered by the backend when the degree's own
 * tuition is < 30% of the peer median — so it never appears on Oxford, MIT, or
 * any degree where the framing would be misleading.
 *
 * Renders as one card in the 6-conclusions grid on Degree Detail. Callers pass
 * the degree id; the component fetches and self-manages loading/hidden state.
 */
export default function CostGapCard({ degreeId }) {
  const [data, setData] = useState(null);
  const { format } = useCurrency();

  useEffect(() => {
    if (!degreeId) return;
    let cancelled = false;
    api
      .get(`/degrees/${encodeURIComponent(degreeId)}/cost-gap`)
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      });
    return () => {
      cancelled = true;
    };
  }, [degreeId]);

  if (!data || !data.eligible) return null;

  const { peers, this_degree } = data;
  const uk = peers?.uk;
  const us = peers?.us;

  // Prefer the LARGER of the two country savings for the headline number —
  // that's usually the US, but not always (e.g. private US CS at $80k+/yr).
  const totalSavings = [uk?.savings?.total_degree_delta_usd, us?.savings?.total_degree_delta_usd]
    .filter((v) => typeof v === "number" && v > 0);
  const headlineTotal = totalSavings.length ? Math.max(...totalSavings) : null;

  const line = (peer, code, name) => {
    const s = peer?.savings;
    if (!s || !s.annual_tuition_delta_usd) return null;
    const modeTag = peer.match_mode === "subject" ? "same subject" : "country avg";
    return (
      <div
        data-testid={`cost-gap-line-${code}`}
        className="flex items-baseline justify-between text-[11px] mt-1.5 leading-snug"
      >
        <span className="text-stone-600">
          vs {name} <span className="text-stone-400 font-underground-sc">· {modeTag}</span>
        </span>
        <span className="font-mono text-stone-900 tabular-nums">
          −{format(s.annual_tuition_delta_usd)}/yr
        </span>
      </div>
    );
  };

  const totalLine = (peer, code, name) => {
    const s = peer?.savings;
    if (!s || !s.total_degree_delta_usd) return null;
    return (
      <div
        data-testid={`cost-gap-total-${code}`}
        className="flex items-baseline justify-between text-[11px] leading-snug"
      >
        <span className="text-stone-600">Total saved vs {name}</span>
        <span className="font-mono text-emerald-800 font-semibold tabular-nums">
          {format(s.total_degree_delta_usd)}
        </span>
      </div>
    );
  };

  return (
    <Card
      data-testid={`cost-gap-card-${degreeId}`}
      className="p-5 border-emerald-300 bg-gradient-to-br from-emerald-50 to-white col-span-1 md:col-span-3"
    >
      <div className="flex items-center gap-2">
        <PiggyBank className="h-4 w-4 text-emerald-700" />
        <div className="label-uppercase !text-[10px] text-emerald-800">Cost gap vs UK / US</div>
      </div>

      <div className="mt-2 flex items-baseline gap-2 flex-wrap">
        <div className="font-chip text-2xl font-semibold text-emerald-800">
          {headlineTotal ? `Save up to ${format(headlineTotal)}` : "Dramatically cheaper"}
        </div>
        <span className="text-[11px] text-stone-500">across a {this_degree.duration_years}-yr degree</span>
      </div>

      <div className="mt-3 grid md:grid-cols-2 gap-x-6 gap-y-1 border-t border-emerald-200/60 pt-3">
        <div>
          <div className="label-uppercase !text-[9px] text-stone-500 mb-0.5">Annual tuition</div>
          <div className="font-mono text-lg font-semibold text-stone-900">
            {format(this_degree.tuition_annual_usd)}
            <span className="text-[10px] text-stone-500 ml-1 font-normal">/yr</span>
          </div>
          {line(uk, "uk", "UK")}
          {line(us, "us", "US")}
        </div>
        <div>
          <div className="label-uppercase !text-[9px] text-stone-500 mb-0.5">Total degree cost</div>
          <div className="font-mono text-lg font-semibold text-stone-900">
            {this_degree.total_degree_usd != null ? format(this_degree.total_degree_usd) : <span data-testid="cost-gap-total-incomplete" className="text-sm font-normal text-stone-500">Incomplete · no comparable total</span>}
          </div>
          <div className="mt-1.5 space-y-1">
            {totalLine(uk, "uk", "UK")}
            {totalLine(us, "us", "US")}
          </div>
        </div>
      </div>

      <p className="mt-3 text-[10px] text-stone-500 leading-snug">
        Living costs (rent, food, insurance, travel) are not included on either side — the gap is
        a like-for-like comparison of degree-level fees only. Match mode shows whether we found
        ≥3 same-subject peers or fell back to the country-wide average.
      </p>
    </Card>
  );
}
